import { liveCheckSelectors } from 'wanderu-ducks-live-check';
import { createAppSelector } from 'wanderu-ducks-utils';

import {
    selectAllTripIds,
    selectLegIds,
    selectTripArriveData,
    selectTripCarrierData,
    selectTripData,
    selectTripDataPricing,
    selectTripDepartData,
    selectTripDuration,
    selectTripInformation,
    selectTripItinerary,
    selectTripNumberOfMidnightsPassed,
} from './baseTripSelectors';
import {
    buildLocalizedTime,
    calculatePricingInUSD,
    flattenTripModes,
    getAccessibilityData,
    getPromotions,
    getRoutedDuration,
    getRoutedTripModes,
    getSingleDuration,
    getSingleTripPricing,
    isRoutedTrip,
} from './helpers';

const FEATURE_BLACKLIST = [
    'luxury',
    'bikes_allowable',
    'next_gen',
    'pets_allowable',
];

function mapTripData(tripData, pricingData) {
    if (!tripData || !tripData.trip_id) {
        return undefined;
    }

    const {
        _perSeatPricing,
        fees,
        pricing,
    } = pricingData;
    const {
        accessibility,
        arrive_datetime: arriveDateTime,
        arrive_datetime_utc: arriveDateTimeUTC,
        arrive_id: arriveId,
        arrive_lat_long: arriveLatLong,
        arrive_name: arriveName,
        arrive_timezone: arriveTimezone,
        arrive_timezone_offset: arriveTimezoneOffset,
        carrier,
        created,
        depart_datetime: departDateTime,
        depart_datetime_utc: departDateTimeUTC,
        depart_id: departId,
        depart_lat_long: departLatLong,
        depart_name: departName,
        depart_timezone: departTimezone,
        depart_timezone_offset: departTimezoneOffset,
        features = {},
        promotions,
        reviews,
        source,
        trip_id: tripId,
        triplegs,
        vehicle_types: modes,
    } = tripData;

    const sortedAndFilteredFeatures = Object.keys(features)
        .filter((key) => Boolean(features[key]) && !FEATURE_BLACKLIST.includes(key))
        .sort((a, b) => a.localeCompare(b, 'en')); // consistent order across all locales
    const isRouted = isRoutedTrip(tripData);
    const tripLegs = mapTripLegs(triplegs, pricingData);
    const tripModes = isRouted
        ? getRoutedTripModes(tripLegs.filtered)
        : (flattenTripModes(modes) || []); // backend bug
    const appliedPromotions = getPromotions(promotions, isRouted);
    const accessibilityInfo = getAccessibilityData(accessibility);

    const pricingInUSD = calculatePricingInUSD(_perSeatPricing);

    const localizedArriveDateTime = buildLocalizedTime({
        dateTime: arriveDateTime,
        dateTimeUTC: arriveDateTimeUTC,
        timezone: arriveTimezone,
        timezoneOffset: arriveTimezoneOffset,
    });

    const localizedDepartDateTime = buildLocalizedTime({
        dateTime: departDateTime,
        dateTimeUTC: departDateTimeUTC,
        timezone: departTimezone,
        timezoneOffset: departTimezoneOffset,
    });

    const duration = isRoutedTrip(tripData)
        ? getRoutedDuration(tripData)
        : getSingleDuration(tripData);

    const isLuxury = features.luxury || false;
    const isNextGen = features?.next_gen || false;

    return {
        _priceInUSDForSorting: pricingInUSD,
        accessibility: accessibilityInfo,
        arriveDateTimeUTC,
        arriveId,
        arriveLatLong,
        arriveName,
        carrierCode: carrier,
        created,
        departDateTimeUTC,
        departId,
        departLatLong,
        departName,
        duration,
        features: sortedAndFilteredFeatures,
        fees,
        isLuxury,
        isNextGen,
        isRouted,
        localizedArriveDateTime,
        localizedDepartDateTime,
        pricing,
        promotions: appliedPromotions,
        reviews,
        transfers: tripData.transfers,
        tripId,
        tripLegs,
        tripModes,
        tripSource: source?.type || 'unknown',
    };
}

function mapTripLegs(tripLegs, pricingData) {
    if (!tripLegs || !tripLegs.length) {
        return [];
    }

    let legIdx = 0;
    return tripLegs.reduce((acc, segment) => {
        const legPricingData = {
            _perSeatPricing: [pricingData._perSeatPricing[legIdx]],
            pricing: [pricingData.pricing[legIdx]],
        };

        const leg = segment.trip_id
            /* select data out of these too - make sure everyone has the same shape */
            ? mapTripData(segment, legPricingData)
            : segment;

        /* "all" means "both travel legs and transfer/layover instances".  when trip
         * details for a routed trip are viewed, we show travel legs separated by
         * transfers and layovers */
        acc.all = [
            ...acc.all,
            leg,
        ];

        if (segment.trip_id) {
            legIdx += 1;
            /* "filtered" means "with all the transfers and layovers taken out".  we do a
             * few transforms which only apply to actual legs of trips and not the
             * layovers/transfers, so this is purely for convenience */
            acc.filtered = [
                ...acc.filtered,
                leg,
            ];
        }

        return acc;
    }, { all: [], filtered: [] });
}

const selectTrip = createAppSelector(
    [
        (state, tripId) => selectTripData(state, tripId),
        (state, tripId, seats, wheelchairs) => selectTripPrices(state, tripId, seats, wheelchairs),
    ],
    (tripData, pricing) => mapTripData(tripData, pricing),
);

function selectAllTrips(state) {
    const tripIds = selectAllTripIds(state);
    return tripIds.reduce((obj, tripId) => ({
        ...obj,
        [tripId]: selectTrip(state, tripId),
    }), {});
}

function selectHasFlight(state, tripId) {
    const trips = selectTripSet(state, tripId);
    return trips.some((trip) => trip.tripModes.includes('flight'));
}

function selectTripSet(state, tripIds) {
    const legIds = selectLegIds(state, tripIds);

    if (!legIds || !legIds.length) {
        return [];
    }

    return legIds
        .map((legId) => selectTrip(state, legId))
        .filter(Boolean);
}

function selectLiveCheckPrices(state, opts) {
    const totalPricing = selectLiveCheckTotalPrices(state, opts) || [];
    if (totalPricing.length === 0) {
        return undefined;
    }

    let mappedPricing = totalPricing;

    if (opts.showTotalPrice) {
        mappedPricing = totalPricing.map(pricingObj => {
            return Object.entries(pricingObj).reduce((acc, [cur, pricing]) => {
                acc[cur] = acc[cur] ?? { price: pricing.price };
                const totalFees = Object.values(pricing.fees || {})
                    .reduce((total, feeValue) => total + feeValue, 0);
                const totalTaxes = Object.values(pricing.taxes || {})
                    .reduce((total, taxValue) => total + taxValue, 0);
                acc[cur].price += totalFees;
                acc[cur].price += totalTaxes;

                return acc;
            }, {});
        });
    }

    return mappedPricing.reduce((arr, lcPricing) => ([
        ...arr,
        ...getSingleTripPricing(lcPricing),
    ]), []);
}

function selectLiveCheckTotalPrices(state, opts) {
    const {
        tripId,
        seats,
        wheelchairs,
    } = opts;

    const legIds = selectLegIds(state, tripId);
    const pricing = legIds.reduce((arr, id) => {
        const { selectedFare } = selectTripData(state, id) || {};
        const lcParams = {
            seats,
            selectedFare,
            tripId: id,
            wheelchairs,
        };

        const lcPricing = liveCheckSelectors.selectLiveCheckPrice(state, lcParams);
        if (!lcPricing) {
            return arr;
        }

        return [...arr, lcPricing];
    }, []);

    if (pricing.length === 0) {
        return undefined;
    }

    return pricing;
}

function selectTripPrices(state, tripId, seats = 1, wheelchairs = 0, showTotalPrice = false) {
    if (!tripId) {
        return {};
    }
    const legIds = selectLegIds(state, tripId);
    return legIds.reduce((obj, legId) => {
        const { fees, pricing: basePricing } = selectTripDataPricing(state, legId) || {};
        const lcPricing = selectLiveCheckPrices(state, {
            seats,
            showTotalPrice,
            tripId: legId,
            wheelchairs,
        });

        if (!basePricing && !lcPricing) {
            return obj;
        }

        // Pax-aware pricing: trip.seats > 1 means basePricing is an N-pax total,
        // not a per-seat price. Normalise once here so every branch below can use
        // the result without repeating the division. Division may produce a float
        // (e.g. 100/3 = 33.33) — display paths use Math.round; sort-only paths
        // (_perSeatPricing → _priceInUSDForSorting) leave the float as-is.
        // When trip.seats is absent or "1", parseInt returns NaN or 1 — both
        // satisfy tripSeats > 1 === false, so normalizedSeats collapses to 1 and
        // the legacy per-seat multiply path is preserved for non-AMT trips.
        const tripSeats = Number.parseInt(selectTripData(state, legId)?.seats, 10);
        const normalizedSeats = tripSeats > 1 ? tripSeats : 1;

        if (seats === 1 && wheelchairs === 0) {
            const pricing = lcPricing || basePricing.map((priceObj) => Object.entries(priceObj).reduce((acc, [currency, price]) => ({
                ...acc,
                [currency]: Math.round(price / normalizedSeats),
            }), {}));
            return {
                _perSeatPricing: [...obj._perSeatPricing, ...pricing],
                fees,
                pricing: [...obj.pricing, ...pricing],
            };
        }

        if (lcPricing) {
            // Normalise basePricing to per-seat for sort consistency (same as other branches).
            // Filter out null/undefined elements — trip.pricing===null produces [undefined].
            const perSeatPricing = (basePricing ?? [])
                .filter(Boolean)
                .map((priceObj) => Object.entries(priceObj).reduce((acc, [currency, price]) => ({
                    ...acc,
                    [currency]: price / normalizedSeats,
                }), {}));
            return {
                _perSeatPricing: [...obj._perSeatPricing, ...perSeatPricing],
                fees,
                pricing: [...obj.pricing, ...lcPricing],
            };
        }

        // Note: `seats` is expected to be a number at this call site — callers go through
        // buildPSearchParams which coerces. If ever called with a string, tripSeats === seats
        // would be false (2 === "2") and the multiply path fires instead. Not guarded here
        // to avoid re-parsing a value that's already an integer in every normal call.
        if (tripSeats === seats) {
            const perSeatPricing = basePricing.map((priceObj) => Object.entries(priceObj).reduce((acc, [currency, price]) => ({
                ...acc,
                [currency]: price / normalizedSeats,
            }), {}));
            return {
                _perSeatPricing: [...obj._perSeatPricing, ...perSeatPricing],
                fees,
                pricing: [...obj.pricing, ...basePricing],
            };
        }

        // Temporary fallback: when psearch hasn't returned fresh results yet after a
        // pax change, we normalise to per-seat then scale to the requested count.
        // UI paths now resubmit psearch on pax change (pservs#594), so this branch
        // fires only briefly — until wapi#401 ships consistent per-pax seat results
        // across all carriers, at which point this block can be removed entirely.
        const perSeatPricing = basePricing.map((priceObj) => Object.entries(priceObj).reduce((acc, [currency, price]) => ({
            ...acc,
            [currency]: price / normalizedSeats,
        }), {}));
        const multipliedPricing = perSeatPricing.map((priceObj) => Object.entries(priceObj).reduce((acc, [currency, price]) => ({
            ...acc,
            [currency]: Math.round(price * seats),
        }), {}));

        return {
            _perSeatPricing: [...obj._perSeatPricing, ...perSeatPricing],
            fees,
            pricing: [...obj.pricing, ...multipliedPricing],
        };
    }, {
        _perSeatPricing: [],
        fees: [],
        pricing: [],
    });
}

function selectAvailableFares(state, tripId, seats = 1, wheelchairs = 0) {
    const legIds = selectLegIds(state, tripId);

    return legIds.reduce((obj, id) => {
        const { selectedFare } = selectTripData(state, id) || {};
        const lcParams = {
            seats,
            selectedFare,
            tripId: id,
            wheelchairs,
        };

        const availableFares = liveCheckSelectors.selectAvailableFares(state, lcParams) || [];
        const hasOnlyCoachClass = availableFares.length === 1
            && availableFares[0]?.wanderu_type === 'coach';

        if (!!availableFares.length && hasOnlyCoachClass) {
            return obj;
        }

        return { ...obj, [id]: availableFares };
    }, {});
}

export {
    mapTripData,
    selectAllTripIds,
    selectAllTrips,
    selectAvailableFares,
    selectHasFlight,
    selectLegIds,
    selectTrip,
    selectTripArriveData,
    selectTripCarrierData,
    selectTripData,
    selectTripDepartData,
    selectTripDuration,
    selectTripInformation,
    selectTripItinerary,
    selectTripNumberOfMidnightsPassed,
    selectTripPrices,
    selectTripSet,
};
